import React, { useState } from 'react';
import SEO from '../components/SEO';
import seoData from '../config/seoData';
import { Phone, MessageCircle, MapPin, CheckCircle, Wrench, Warehouse, Truck, Factory, Menu, X, ChevronRight, ArrowRight, Clock, Shield, Zap, Instagram } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { useNavigate, Link } from 'react-router-dom';

const LimpezaPosObraGalpoes = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleWhatsAppClick = (contexto = 'galpao') => {
    const mensagens = {
      galpao: 'Olá! Preciso de limpeza pós-obra para galpão. Gostaria de receber um orçamento.',
      orcamento: 'Olá! Gostaria de solicitar uma avaliação e orçamento de limpeza pós-obra para galpão.',
      info: 'Olá! Gostaria de saber mais sobre limpeza pós-obra de galpões.'
    };
    
    const message = encodeURIComponent(mensagens[contexto] || mensagens.galpao);
    window.open(`https://wa.me/5531971823249?text=${message}`, '_blank');
  };

  const tiposGalpao = [
    {
      title: 'Galpões Logísticos',
      description: 'Centros de distribuição e armazéns após construção ou reforma. Foco em produtividade e preparo rápido para operação.',
      icon: <Warehouse className="w-8 h-8" />,
      features: ['Grandes metragens', 'Piso de concreto', 'Docas e plataformas', 'Áreas de circulação']
    },
    {
      title: 'Galpões Industriais',
      description: 'Áreas de produção e manufatura após obras civis ou adequações. Remoção de resíduos pesados e sujeira industrial.',
      icon: <Factory className="w-8 h-8" />,
      features: ['Piso industrial', 'Remoção de cimento', 'Limpeza pesada', 'Normas técnicas']
    },
    {
      title: 'Galpões Comerciais',
      description: 'Espaços comerciais de grande porte como lojas, showrooms e mercados após finalização de obra.',
      icon: <Truck className="w-8 h-8" />,
      features: ['Preparo para inauguração', 'Limpeza de vitrines', 'Áreas externas', 'Estacionamentos']
    }
  ];

  const desafiosGalpao = [
    {
      desafio: 'Grandes Metragens',
      solucao: 'Equipamentos mecanizados de alta produtividade',
      icon: <Zap className="w-6 h-6" />
    },
    {
      desafio: 'Prazos Apertados',
      solucao: 'Equipe dimensionada e planejamento logístico',
      icon: <Clock className="w-6 h-6" />
    },
    {
      desafio: 'Resíduos Pesados',
      solucao: 'Equipamentos profissionais e produtos adequados',
      icon: <Wrench className="w-6 h-6" />
    },
    {
      desafio: 'Acesso e Circulação',
      solucao: 'Equipamentos compactos e planejamento de rota',
      icon: <Shield className="w-6 h-6" />
    }
  ];

  const faqGalpoes = [
    {
      question: 'Quanto tempo demora para limpar um galpão pós-obra?',
      answer: 'O prazo depende da metragem e das condições do piso. Um galpão de 1.000m² pode levar de 1 a 2 dias. Galpões acima de 5.000m² podem levar de 3 a 5 dias. Após avaliação presencial, fornecemos um cronograma detalhado.'
    },
    {
      question: 'Qual o custo médio por m² para limpeza de galpão?',
      answer: 'O valor varia conforme o nível de sujeira, tipo de piso, acesso e cidade. Em galpões logísticos com piso de concreto, o investimento médio fica entre R$ 3,50 e R$ 8,00 por m². Áreas com resíduos pesados de cimento podem ter custo maior. Solicite orçamento com metragem e fotos para receber valor preciso.'
    },
    {
      question: 'Vocês trabalham em galpões em operação?',
      answer: 'Sim. Podemos realizar a limpeza pós-obra em horários alternativos (noite, finais de semana) para não interferir na operação logística ou industrial. Planejamos junto com sua equipe o melhor momento para executar o serviço.'
    },
    {
      question: 'Qual equipamento é usado em galpões?',
      answer: 'Utilizamos lavadoras de piso de grande porte (varredeiras-lavadoras combinadas) que limpam e secam simultaneamente, varredeiras industriais para remoção de poeira e resíduos sólidos, e aspiradores profissionais para áreas específicas. A seleção depende da metragem e tipo de piso.'
    },
    {
      question: 'Fazem limpeza de áreas externas e estacionamentos?',
      answer: 'Sim. Além da área interna do galpão, realizamos limpeza de pátios, estacionamentos, docas, rampas e áreas externas após obras. Utilize equipamentos adequados para cada tipo de superfície.'
    },
    {
      question: 'Atendem galpões fora de Belo Horizonte?',
      answer: 'Sim. Atendemos toda a Região Metropolitana de Belo Horizonte, incluindo Contagem, Betim, Nova Lima, Mário Campos e municípios próximos. Para localidades mais distantes, avaliamos a viabilidade técnica e logística.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title={seoData['/limpeza-pos-obra-galpoes'].title}
        description={seoData['/limpeza-pos-obra-galpoes'].description}
        canonical={seoData['/limpeza-pos-obra-galpoes'].canonical}
      />

      {/* Header */}
      <header className="bg-slate-900 text-white py-4 px-6 sticky top-0 z-40 shadow-lg">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
            <img 
              src="https://customer-assets.emergentagent.com/job_locacao-pisos/artifacts/9nveujmi_file_00000000e2f071f5a40c0fc04e1c9bed.png" 
              alt="WellTec Logo" 
              className="h-16 w-auto"
            />
          </div>
          
          <div className="hidden md:flex items-center space-x-6">
            <Link to="/" className="hover:text-blue-400 transition-colors">Início</Link>
            <Link to="/servicos" className="hover:text-blue-400 transition-colors">Serviços</Link>
            <Link to="/limpeza-pos-obra" className="text-green-400 font-semibold">Limpeza Pós-Obra</Link>
            <Link to="/locacao-lavadora-piso" className="hover:text-blue-400 transition-colors">Locação</Link>
            <Link to="/blog" className="hover:text-blue-400 transition-colors">Blog</Link>
            <a href="https://www.instagram.com/welltecbrasil/" target="_blank" rel="noopener noreferrer" className="hover:text-blue-400 transition-colors">
              <Instagram className="w-5 h-5" />
            </a>
            <Button onClick={handleWhatsAppClick} className="bg-green-500 hover:bg-green-600">
              <MessageCircle className="w-4 h-4 mr-2" />
              WhatsApp
            </Button>
          </div>

          <button className="md:hidden text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-slate-900 border-t border-slate-700 shadow-xl">
            <div className="flex flex-col space-y-1 p-4">
              <button onClick={() => { navigate('/'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors">Início</button>
              <button onClick={() => { navigate('/servicos'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors">Serviços</button>
              <button onClick={() => { navigate('/limpeza-pos-obra'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors text-green-400 font-semibold">Limpeza Pós-Obra</button>
              <button onClick={() => { navigate('/locacao-lavadora-piso'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors">Locação</button>
              <button onClick={() => { navigate('/blog'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors">Blog</button>
              <Button onClick={() => { handleWhatsAppClick(); setMobileMenuOpen(false); }} className="bg-green-500 hover:bg-green-600 w-full mt-2">
                <MessageCircle className="w-4 h-4 mr-2" />
                WhatsApp
              </Button>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section 
        className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 text-white py-24 md:py-32 overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.90), rgba(30, 41, 59, 0.90)), url('https://customer-assets-4nw71qhi.emergentagent.net/job_locacao-pisos/artifacts/sqhpxfrr_Limpeza-pos%20obra%20lavadora%20de%20piso%20galpao.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl">
            <div className="inline-block bg-green-600 text-white px-4 py-2 rounded-full text-sm font-bold mb-6">
              ESPECIALISTAS EM GRANDES ÁREAS
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Limpeza Pós-Obra de Galpões em BH e Região
            </h1>
            
            <p className="text-xl md:text-2xl text-blue-100 mb-6 leading-relaxed font-semibold">
              Limpeza mecanizada profissional para galpões logísticos, industriais e comerciais após obras e reformas
            </p>

            <p className="text-lg text-gray-300 mb-8 max-w-3xl">
              Atuamos em grandes metragens utilizando equipamentos de alta produtividade para preparar seu galpão para início de operação, armazenagem ou recebimento de mercadorias.
            </p>

            <p className="text-sm text-blue-200 mb-10 flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              BH, Contagem, Betim, Nova Lima e Região Metropolitana
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={() => handleWhatsAppClick('orcamento')}
                size="lg"
                className="bg-green-500 hover:bg-green-600 text-white px-8 py-6 text-lg shadow-xl hover:scale-105 transition-all"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                Solicitar Orçamento para Galpão
              </Button>
              
              <Button 
                onClick={() => navigate('/limpeza-pos-obra')}
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-slate-900 px-8 py-6 text-lg"
              >
                Ver Todos os Serviços
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Por Que Galpões Precisam de Limpeza Mecanizada */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Por Que Galpões Exigem Limpeza Mecanizada?
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Em grandes áreas, a limpeza manual se torna inviável pela baixa produtividade, alto custo de mão de obra e qualidade inconsistente. A mecanização é a solução profissional.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <Card className="border-2 border-red-200 bg-red-50">
                <CardContent className="p-8">
                  <div className="bg-red-600 text-white w-14 h-14 rounded-full flex items-center justify-center mb-6 mx-auto">
                    <X className="w-7 h-7" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Limpeza Manual</h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start gap-2">
                      <span className="text-red-600 mt-1">•</span>
                      <span>Baixa produtividade em grandes áreas</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-600 mt-1">•</span>
                      <span>Prazo de execução muito longo</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-600 mt-1">•</span>
                      <span>Alto custo de mão de obra</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-600 mt-1">•</span>
                      <span>Resultado inconsistente</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-600 mt-1">•</span>
                      <span>Dificuldade com sujeira aderida</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-2 border-green-500 shadow-xl bg-green-50">
                <CardContent className="p-8">
                  <div className="bg-green-600 text-white w-14 h-14 rounded-full flex items-center justify-center mb-6 mx-auto">
                    <CheckCircle className="w-7 h-7" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Limpeza Mecanizada WellTec</h3>
                  <ul className="space-y-3 text-gray-800">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span><strong>Alta produtividade</strong> em grandes metragens</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span><strong>Prazo reduzido</strong> de execução</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span><strong>Custo-benefício</strong> superior</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span><strong>Resultado uniforme</strong> e profissional</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span><strong>Remoção eficiente</strong> de resíduos pesados</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Tipos de Galpão que Atendemos */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Tipos de Galpão que Atendemos
            </h2>
            <p className="text-lg text-gray-600">
              Experiência em diferentes segmentos e necessidades
            </p>
          </div>

          <div className="max-w-6xl mx-auto space-y-6">
            {tiposGalpao.map((tipo, index) => (
              <Card key={index} className="border-2 border-gray-100 hover:border-green-500 hover:shadow-xl transition-all">
                <CardContent className="p-8">
                  <div className="flex flex-col md:flex-row gap-6 items-start">
                    <div className="bg-green-100 text-green-600 w-16 h-16 rounded-lg flex items-center justify-center flex-shrink-0">
                      {tipo.icon}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">{tipo.title}</h3>
                      <p className="text-gray-600 mb-4 text-lg">{tipo.description}</p>
                      <div className="flex flex-wrap gap-2">
                        {tipo.features.map((feature, idx) => (
                          <span key={idx} className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold">
                            ✓ {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-10">
            <Button 
              onClick={() => handleWhatsAppClick('info')}
              size="lg"
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-6"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              Falar Sobre Meu Galpão
            </Button>
          </div>
        </div>
      </section>

      {/* Desafios e Soluções */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Desafios de Galpões e Nossas Soluções
              </h2>
              <p className="text-lg text-gray-600">
                Conhecemos os desafios técnicos e oferecemos soluções comprovadas
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {desafiosGalpao.map((item, index) => (
                <Card key={index} className="border-2 border-gray-100 hover:border-blue-500 transition-all">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="bg-blue-100 text-blue-600 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                        {item.icon}
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-gray-900 mb-2">Desafio: {item.desafio}</h4>
                        <p className="text-gray-600">
                          <strong className="text-green-600">Solução:</strong> {item.solucao}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Orçamento Rápido */}
      <section className="py-16 bg-gradient-to-r from-green-600 to-green-700 text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Precisa Preparar Seu Galpão Para Operação?
          </h2>
          <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
            Informe a metragem aproximada e a cidade para receber uma avaliação personalizada em minutos
          </p>
          <Button 
            onClick={() => handleWhatsAppClick('orcamento')}
            size="lg"
            className="bg-white text-green-600 hover:bg-gray-100 px-10 py-6 text-lg font-bold"
          >
            <MessageCircle className="w-5 h-5 mr-2" />
            Solicitar Orçamento Agora
          </Button>
        </div>
      </section>

      {/* Conteúdos Relacionados */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Conteúdos Relacionados
              </h2>
              <p className="text-lg text-gray-600">
                Saiba mais sobre limpeza pós-obra profissional
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <Card className="border-2 border-gray-100 hover:border-green-500 hover:shadow-xl transition-all cursor-pointer" onClick={() => navigate('/limpeza-pos-obra')}>
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">
                    Limpeza Pós-Obra Completa
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Conheça todos os nossos serviços de limpeza após obras e reformas
                  </p>
                  <span className="text-green-600 font-semibold text-sm flex items-center gap-2">
                    Ver serviços <ArrowRight className="w-4 h-4" />
                  </span>
                </CardContent>
              </Card>

              <Card className="border-2 border-gray-100 hover:border-green-500 hover:shadow-xl transition-all cursor-pointer" onClick={() => navigate('/blog/limpeza-galpao-industrial')}>
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">
                    Guia: Limpeza de Galpão Industrial
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Artigo completo sobre equipamentos e técnicas para galpões
                  </p>
                  <span className="text-green-600 font-semibold text-sm flex items-center gap-2">
                    Ler artigo <ArrowRight className="w-4 h-4" />
                  </span>
                </CardContent>
              </Card>

              <Card className="border-2 border-gray-100 hover:border-green-500 hover:shadow-xl transition-all cursor-pointer" onClick={() => navigate('/locacao-lavadora-piso')}>
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">
                    Locação de Equipamentos
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Prefere alugar os equipamentos? Veja nossas opções de locação
                  </p>
                  <span className="text-blue-600 font-semibold text-sm flex items-center gap-2">
                    Ver locação <ArrowRight className="w-4 h-4" />
                  </span>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Perguntas Frequentes sobre Limpeza de Galpões
              </h2>
            </div>

            <Accordion type="single" collapsible className="space-y-4">
              {faqGalpoes.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="bg-slate-50 border-2 border-gray-100 rounded-lg px-6">
                  <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-green-600">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 max-w-6xl mx-auto">
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_locacao-pisos/artifacts/9nveujmi_file_00000000e2f071f5a40c0fc04e1c9bed.png" 
                alt="WellTec" 
                className="h-16 mb-4"
              />
              <p className="text-gray-400 text-sm">
                Limpeza pós-obra profissional para galpões em BH e região.
              </p>
            </div>

            <div>
              <h3 className="font-bold mb-4">Serviços</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/limpeza-pos-obra" className="hover:text-green-400 transition-colors">Limpeza Pós-Obra</Link></li>
                <li><Link to="/limpeza-pos-obra-galpoes" className="hover:text-green-400 transition-colors">Limpeza de Galpões</Link></li>
                <li><Link to="/locacao-lavadora-piso" className="hover:text-green-400 transition-colors">Locação de Equipamentos</Link></li>
                <li><Link to="/blog" className="hover:text-green-400 transition-colors">Blog</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold mb-4">Empresa</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/" className="hover:text-green-400 transition-colors">Início</Link></li>
                <li><Link to="/servicos" className="hover:text-green-400 transition-colors">Serviços</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold mb-4">Contato</h3>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li>(31) 97182-3249</li>
                <li>BH e Região Metropolitana</li>
                <li>
                  <a href="https://www.instagram.com/welltecbrasil/" target="_blank" rel="noopener noreferrer" className="hover:text-green-400 transition-colors">
                    @welltecbrasil
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-gray-400 text-sm">
            <p>&copy; 2025 WellTec - Limpeza Pós-Obra Profissional. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LimpezaPosObraGalpoes;
